// ───────────────────────────────────────────────────────────────
// store/slices/listSlice.ts
// ───────────────────────────────────────────────────────────────
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import useApi from '../../services/useApi';

/* ------------------------------------------------------------------ */
/*   Interfaces / Types                                              */
/* ------------------------------------------------------------------ */
export interface Row {
  _clientId?: string;             
  id?: string;
  ticket_id?: string;
  user_id?: string;
  user_type?: string;
  email?: string;
  reason?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  assignee_user_id?: string | null;
  assignee_user_type?: string | null; 
  province?: string | null;
  region?: string | null;
  district?: string | null;
  channel?: string | null;
  priority?: string | null;
  user_name?: string;
  user_code?: string;
  assignee_name?: string;
  assignee_user_code?: string;
  [key: string]: any;
}

interface ListState {
  loading: boolean;
  error: string | null;
  success: string | null;

  data: Row[];
  recordCount?: number;
  statusCode?: number;
  upfrontServiceFee?: number;
  entireResponse?: any;
  /** Cursor-based pagination: next cursor and whether more pages exist */
  nextCursor?: string | null;
  hasMore?: boolean;
  /** Page index for the last fulfilled request (cursor pagination: append only in order) */
  lastFulfilledPageIndex?: number | null;
  /** All previous cursors: [null, C1, C2, ...] so page_number in URL gives correct index after refresh */
  cursorHistory: (string | null)[];

  staticData: Row[];
  
  // Request tracking to ignore stale responses
  currentRequestId?: string;
}

export interface ApiResponse {
  statusCode: number | undefined;
  error: boolean;
  data: any;
}

interface ErrorPayload {
  message: string;
}

/* ------------------------------------------------------------------ */
/*   Helper Functions                                                */
/* ------------------------------------------------------------------ */
const generateId = () => `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const ensureClientIds = (rows: Row[]): Row[] => {
  return rows.map(row => ({
    ...row,
    _clientId: row._clientId || generateId(),
  }));
};

/* ------------------------------------------------------------------ */
/*   Initial state                                                   */
/* ------------------------------------------------------------------ */
const initialState: ListState = {
  loading: true,
  error: null,
  success: null,

  data: [],
  recordCount: undefined,
  statusCode: undefined,
  upfrontServiceFee: undefined,
  entireResponse: undefined,
  nextCursor: null,
  hasMore: false,
  lastFulfilledPageIndex: null,
  cursorHistory: [null],

  staticData: [],
  
  currentRequestId: undefined,
};

/* ------------------------------------------------------------------ */
/*   Async thunk – fetch list                                        */
/* ------------------------------------------------------------------ */
interface FetchListArgs {
  url: string;
  requestId: string;
  /** Cursor pagination: page index requested (0 = first page) so we append next_cursor in order */
  pageIndex?: number;
}

export const fetchList = createAsyncThunk<
  ApiResponse & { requestId: string },
  string | FetchListArgs,
  { rejectValue: ErrorPayload & { requestId?: string } }
>('list/fetch', async (args, { rejectWithValue }) => {
  // Support both string (legacy) and object format
  const url = typeof args === 'string' ? args : args.url;
  const requestId = typeof args === 'string' ? generateId() : args.requestId;
  
  try {
    const api = useApi();
    const apiRes = await api.getAPI(url);

    // Map admin support tickets response
    if (url.includes('admin-support')) {
      return {
        ...apiRes,
        statusCode: 200,
        requestId,
        data: {
          data: apiRes.data.tickets,
          total_count: apiRes.data.total_count,
          summary: apiRes.data.summary,
          max_upfront_service_fee_percent: apiRes.data.max_upfront_service_fee_percent,
        },
      };
    }

    return { ...apiRes, statusCode: apiRes.data.status, requestId };
  } catch (err: any) {
    console.error('Error fetching list:', err);
    return rejectWithValue({
      message: err?.message || 'Unknown error occurred',
      requestId,
    });
  }
});

/* ------------------------------------------------------------------ */
/*    Slice                                                           */
/* ------------------------------------------------------------------ */
const listSlice = createSlice({
  name: 'list',
  initialState,
  reducers: {
    resetState: () => ({
      ...initialState,
      // Stamp a unique ID so any in-flight response that arrives after this reset
      // is rejected by the fulfilled guard (responseRequestId !== currentRequestId).
      currentRequestId: `reset_${Date.now()}`,
    }),

    // Replace entire staticData with client IDs
    setStaticData: (state, action: PayloadAction<Row[]>) => {
      state.staticData = ensureClientIds(action.payload);
    },

    // Update a single row by client ID
    updateRow: (state, action: PayloadAction<Row>) => {
      const idx = state.staticData.findIndex(r => r._clientId === action.payload._clientId);
      if (idx !== -1) {
        state.staticData[idx] = { 
          ...state.staticData[idx], 
          ...action.payload, 
          _clientId: state.staticData[idx]._clientId 
        };
      }
    },

    // Add a new row
    addRow: (state, action: PayloadAction<Row>) => {
      const newRow = { ...action.payload, _clientId: generateId() };
      state.staticData.push(newRow);
    },

    // Delete a row by client ID
    deleteRow: (state, action: PayloadAction<string>) => {
      state.staticData = state.staticData.filter(r => r._clientId !== action.payload);
    },

    /** Cursor pagination: set full cursor history (e.g. restore from URL or reset) */
    setCursorHistory: (state, action: PayloadAction<(string | null)[]>) => {
      state.cursorHistory = action.payload;
    },

    // Update multiple rows by user_code
    updateRowsByUserCode: (
      state,
      action: PayloadAction<{ userCode: string; changes: Partial<Row> }>
    ) => {
      const { userCode, changes } = action.payload;
      state.staticData = state.staticData.map(row =>
        row.user_code === userCode ? { ...row, ...changes, _clientId: row._clientId } : row
      );
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchList.pending, (state, action) => {
        // Extract requestId from the argument (with defensive check for tests)
        const args = action.meta?.arg;
        let requestId: string | undefined;
        
        if (args) {
          if (typeof args === 'object' && args !== null && 'requestId' in args) {
            // New format with explicit requestId
            requestId = (args as FetchListArgs).requestId;
          }
          // For legacy string format, don't set requestId here
          // The thunk will generate one, and we'll accept it in fulfilled state
          // if currentRequestId is undefined (for backward compatibility)
        }
        
        // Store the current request ID to track which request is active
        // Only set it for new format calls with explicit requestId
        // Legacy calls (string format) will have undefined currentRequestId
        // and will be accepted in fulfilled state for backward compatibility
        state.currentRequestId = requestId;
        
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(fetchList.fulfilled, (state, action) => {
        // Ignore stale responses - reject if response ID doesn't match the current expected ID
        const responseRequestId = action.payload.requestId;

        // If the response has an ID and it doesn't match the current request ID, it's stale.
        // This also covers the case where currentRequestId was cleared to undefined after a
        // previous response was processed — stale in-flight responses arriving late are rejected.
        if (responseRequestId && responseRequestId !== state.currentRequestId) {
          // This is a stale response, ignore it
          return;
        }
        
        state.loading = false;
        if (!action.payload.error) {
          state.data = ensureClientIds(action.payload.data.data);
          state.recordCount = action.payload.data.total_count;
          state.success = 'true';
          state.upfrontServiceFee = action.payload?.data.max_upfront_service_fee_percent;
          state.entireResponse = action.payload?.data;
          state.nextCursor = action.payload.data?.next_cursor ?? null;
          state.hasMore = action.payload.data?.has_more ?? false;
          const args = action.meta?.arg;
          const pageIndex =
            typeof args === 'object' && args != null && 'pageIndex' in args
              ? (args as FetchListArgs).pageIndex
              : null;
          state.lastFulfilledPageIndex = pageIndex;
          if (state.nextCursor != null && pageIndex != null && state.cursorHistory.length === pageIndex + 1) {
            const last = state.cursorHistory[state.cursorHistory.length - 1];
            const alreadyHasCursor = state.cursorHistory.includes(state.nextCursor);
            if (last !== state.nextCursor && !alreadyHasCursor) {
              state.cursorHistory = [...state.cursorHistory, state.nextCursor];
            }
          }
        } else {
          state.success = 'false';
          state.statusCode = action.payload.statusCode;
          state.error = 'Error occurred while fetching list';
        }
        
        // Do NOT clear currentRequestId here. Keeping it set to the last processed ID means
        // any late-arriving stale responses with a different ID are still correctly rejected.
      })
      .addCase(fetchList.rejected, (state, action) => {
        // Ignore stale rejections
        const responseRequestId = action.payload?.requestId;
        if (responseRequestId && responseRequestId !== state.currentRequestId) {
          // This is a stale response, ignore it
          return;
        }
        
        state.loading = false;
        state.data = [];
        state.error = action.payload?.message || 'Unknown error occurred';
        state.success = null;
        state.nextCursor = null;
        state.hasMore = false;
        state.lastFulfilledPageIndex = null;
        state.cursorHistory = [null];
      });
  },
});

/* ------------------------------------------------------------------ */
/*   Exports                                                         */
/* ------------------------------------------------------------------ */
export const {
  resetState,
  setStaticData,
  updateRow,
  addRow,
  deleteRow,
  updateRowsByUserCode,
  setCursorHistory,
} = listSlice.actions;

export default listSlice.reducer;
 